import React, { useState, useEffect } from 'react';
import marketplaceApi from '../marketplaceApi';

function Stars({ rating, size = '' }) {
  return (
    <span style={{ color: '#f59e0b', fontSize: size }}>
      {[1, 2, 3, 4, 5].map(i => (
        <i key={i} className={`${i <= Math.round(rating) ? 'fas' : 'far'} fa-star`} />
      ))}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
  if (d === 0) return 'Today';
  if (d === 1) return 'Yesterday';
  if (d < 30)  return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function MarketplaceReviews() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [page,    setPage]    = useState(1);

  const load = async (p = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await marketplaceApi.get('/reviews', { params: { page: p, limit: 10 } });
      setData(res.data);
      setPage(p);
    } catch {
      setError('Failed to load reviews.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div className="content-wrapper">
      <section className="content pt-4">
        <div className="container-fluid text-center py-5">
          <div className="spinner-border text-primary" />
        </div>
      </section>
    </div>
  );

  if (error) return (
    <div className="content-wrapper">
      <section className="content pt-4">
        <div className="container-fluid"><div className="alert alert-danger">{error}</div></div>
      </section>
    </div>
  );

  // Response shape: { success, total, page, limit, totalPages, avgRating, reviews: [ { id, rating, comment, reviewerName, createdAt } ] }
  const total      = data?.total      || 0;
  const totalPages = data?.totalPages || 1;
  const avg        = data?.avgRating  ? Number(data.avgRating).toFixed(1) : null;
  const reviews    = data?.reviews    || [];

  // Compute star distribution from current page reviews (approximation when no server-side dist)
  const dist = reviews.reduce((acc, r) => {
    const star = Math.round(r.rating);
    acc[star] = (acc[star] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <h1 className="m-0"><i className="fas fa-star mr-2 text-warning" />Reviews</h1>
        </div>
      </div>
      <section className="content">
        <div className="container-fluid">

          {total === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-star fa-2x mb-2 d-block" />
              No reviews yet. Buyers leave reviews after a successful deal.
            </div>
          ) : (
            <>
              {/* Summary card */}
              {avg && (
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="row align-items-center">
                      <div className="col-sm-3 text-center border-right">
                        <div style={{ fontSize: 56, fontWeight: 700, lineHeight: 1 }}>{avg}</div>
                        <Stars rating={Number(avg)} size="1.2rem" />
                        <div className="text-muted small mt-1">{total} review{total !== 1 ? 's' : ''}</div>
                      </div>
                      <div className="col-sm-9 pt-3 pt-sm-0">
                        {[5, 4, 3, 2, 1].map(star => {
                          const count = dist[star] || 0;
                          const pct   = reviews.length > 0 ? Math.round((count / reviews.length) * 100) : 0;
                          return (
                            <div key={star} className="d-flex align-items-center mb-1">
                              <span className="mr-2" style={{ minWidth: 40, fontSize: 13 }}>
                                {star} <i className="fas fa-star text-warning" style={{ fontSize: 11 }} />
                              </span>
                              <div className="progress flex-grow-1 progress-sm mr-2">
                                <div className="progress-bar bg-warning" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-muted small" style={{ minWidth: 28 }}>{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Review list */}
              {reviews.map(r => (
                <div className="card mb-2" key={r.id}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <strong>{r.reviewerName || 'Buyer'}</strong>
                      <small className="text-muted">{timeAgo(r.createdAt)}</small>
                    </div>
                    <Stars rating={r.rating} />
                    {r.comment && <p className="mb-0 mt-2 text-secondary">{r.comment}</p>}
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center mt-3 gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page <= 1}
                    onClick={() => load(page - 1)}
                  >
                    <i className="fas fa-chevron-left" />
                  </button>
                  <span className="text-muted small">Page {page} of {totalPages}</span>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page >= totalPages}
                    onClick={() => load(page + 1)}
                  >
                    <i className="fas fa-chevron-right" />
                  </button>
                </div>
              )}
            </>
          )}

        </div>
      </section>
    </div>
  );
}
